import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import { loadSkinModule } from '../../shared/skin-loader.js';
import * as defaultSkin from './skins/button.default.js';

class RunButton extends skinResolutionMixin(RunElement) {
  // Default skin is always bundled; all others lazy-load via the registry.
  // boston-bricks routes through loadSkinModule() to keep the Vite
  // asset-prebundling workaround intact.
  static defaultSkin = defaultSkin;
  static skinLoaders = {
    wireframe: () => import('./skins/button.wireframe.js'),
    bento: () => import('./skins/button.bento.js'),
    'boston-bricks': () => loadSkinModule('boston-bricks', 'button'),
  };

  // 1. Private field declarations
  #disabled = false;
  #loading = false;
  #formDisabled = false;
  #type = 'button';
  #name = null;
  #value = '';
  #variant = 'primary';
  #size = 'md';
  #skin = null;
  _internals = null;

  #handleClick = (event) => {
    if (this.#disabled || this.#loading || this.#formDisabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.#activate(event);
  };

  #handleKeydown = (event) => {
    if (this.#disabled || this.#loading || this.#formDisabled) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#activate(event);
    } else if (event.key === ' ') {
      event.preventDefault(); // Prevent scroll
    }
  };

  #handleKeyup = (event) => {
    if (this.#disabled || this.#loading || this.#formDisabled) return;
    if (event.key === ' ') {
      this.#activate(event);
    }
  };

  // 2. Static class fields
  static formAssociated = true;
  static componentName = 'Button';
  static version = '1.0.0';
  static category = 'form';
  static layer = 'atomic';
  static parts = ['base', 'label', 'loader', 'icon-start', 'icon-end'];
  static slots = [
    { name: '', description: 'Button label text' },
    { name: 'icon-start', description: 'Leading icon' },
    { name: 'icon-end', description: 'Trailing icon' },
  ];
  static events = [
    { name: 'run-click', description: 'Fired when button is activated via pointer or keyboard' },
  ];

  // 3. observedAttributes
  static get observedAttributes() {
    return ['disabled', 'loading', 'type', 'name', 'value', 'variant', 'size', 'skin', 'aria-label', 'aria-labelledby'];
  }

  // 4. constructor
  constructor() {
    super();
    this._internals = this.attachInternals();
  }

  // 5. Override shadow DOM creation to enable delegatesFocus
  _initializeShadowDOM() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open', delegatesFocus: true });
    }
  }

  // 6. connectedCallback
  connectedCallback() {
    super.connectedCallback?.();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('keyup', this.#handleKeyup);
  }

  // 7. disconnectedCallback
  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('keyup', this.#handleKeyup);
  }

  // 8. attributeChangedCallback
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'disabled': this.disabled = newValue !== null; break;
      case 'loading':  this.loading  = newValue !== null; break;
      case 'type':     this.type     = newValue; break;
      case 'name':     this.name     = newValue; break;
      case 'value':    this.value    = newValue; break;
      case 'variant':  this.variant  = newValue; break;
      case 'size':     this.size     = newValue; break;
      case 'skin':     this.skin     = newValue; break;
      case 'aria-label':
      case 'aria-labelledby':
        this.requestUpdate(); break;
    }
  }

  // 9. Form lifecycle callbacks
  formResetCallback() {
    this.disabled = false;
    this.loading  = false;
    this.value    = '';
  }

  formDisabledCallback(disabled) {
    this.#formDisabled = Boolean(disabled);
    this.requestUpdate();
  }

  // 10. firstUpdated — called once after first render
  firstUpdated(changedProperties) {
    super.firstUpdated?.(changedProperties);
    this._internals.setValidity({});
  }

  // 11. Public property accessors

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.requestUpdate();
  }

  get loading() { return this.#loading; }
  set loading(value) {
    const coerced = Boolean(value);
    if (this.#loading === coerced) return;
    this.#loading = coerced;
    this.toggleAttribute('loading', coerced);
    this.requestUpdate();
  }

  get type() { return this.#type; }
  set type(value) {
    const allowed = ['button', 'submit', 'reset'];
    const str = value == null ? '' : String(value).trim();
    const coerced = allowed.includes(str) ? str : 'button';
    if (this.#type === coerced) return;
    this.#type = coerced;
    this.setAttribute('type', coerced);
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(value) {
    const trimmed = value == null ? '' : String(value).trim();
    const coerced = trimmed || null;
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) {
      this.setAttribute('name', coerced);
    } else {
      this.removeAttribute('name');
    }
    this.#syncFormValue();
    this.requestUpdate();
  }

  get value() { return this.#value; }
  set value(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#value === coerced) return;
    this.#value = coerced;
    if (coerced) {
      this.setAttribute('value', coerced);
    } else {
      this.removeAttribute('value');
    }
    this.#syncFormValue();
    this.requestUpdate();
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const allowed = ['primary', 'secondary', 'ghost', 'danger'];
    const str = value == null ? '' : String(value).trim();
    const coerced = allowed.includes(str) ? str : 'primary';
    if (this.#variant === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    this.requestUpdate();
  }

  get size() { return this.#size; }
  set size(value) {
    const allowed = ['sm', 'md', 'lg'];
    const str = value == null ? '' : String(value).trim();
    const coerced = allowed.includes(str) ? str : 'md';
    if (this.#size === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    this.requestUpdate();
  }

  get skin() { return this.#skin; }
  set skin(value) {
    const trimmed = value == null ? '' : String(value).trim();
    const coerced = trimmed || null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  get ariaLabel() { return this.getAttribute('aria-label'); }
  get ariaLabelledby() { return this.getAttribute('aria-labelledby'); }

  // 12. Skin rendering

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (!this.shadowRoot) return;

    // Skin DOM + stylesheet are delegated to the resolved skin module by
    // skinResolutionMixin._render(), which runs before updated(). The
    // post-skin host/ARIA work below observes the freshly rendered skin DOM.
    const base = this.shadowRoot.querySelector('[part="base"]');
    if (base) {
      const effectivelyDisabled = this.#disabled || this.#formDisabled;
      if (effectivelyDisabled) {
        base.setAttribute('aria-disabled', 'true');
      } else {
        base.removeAttribute('aria-disabled');
      }
      if (this.#loading) {
        base.setAttribute('aria-busy', 'true');
      } else {
        base.removeAttribute('aria-busy');
      }
      // Forward host accessible name to shadow button so axe-core can resolve it
      const hostAriaLabel = this.getAttribute('aria-label');
      const hostAriaLabelledby = this.getAttribute('aria-labelledby');
      if (hostAriaLabel) {
        base.setAttribute('aria-label', hostAriaLabel);
        base.removeAttribute('aria-labelledby');
      } else if (hostAriaLabelledby) {
        base.setAttribute('aria-labelledby', hostAriaLabelledby);
        base.removeAttribute('aria-label');
      } else {
        const labelText = this.textContent?.trim();
        if (labelText) base.setAttribute('aria-label', labelText);
        else base.removeAttribute('aria-label');
        base.removeAttribute('aria-labelledby');
      }
    }

    // Manage CustomStateSet
    const states = this._internals?.states;
    if (states) {
      const effectivelyDisabled = this.#disabled || this.#formDisabled;
      if (effectivelyDisabled) states.add('--disabled');
      else states.delete('--disabled');
      if (this.#loading) states.add('--loading');
      else states.delete('--loading');
    }

    this.renderBoundary();
  }

  // 12b. Extension API (Component Extension API Contract v1.0)

  /**
   * Returns the boundary element for this component.
   * Called by updated() after the skin renders. Override in subclasses to
   * apply full-skin composed-mode boundary rendering that CSS alone cannot express.
   *
   * Composition context available inside an override:
   *   this.hasAttribute('data-ys-composed') — true when parent claims this boundary
   *   this.getAttribute('data-position')    — 'start' | 'end' | null
   *
   * Note: for run-button the boundary element is the <button> itself ([part="base"]),
   * not a separate wrapper. CSS handles composed-mode suppression via data-ys-composed.
   *
   * @returns {HTMLElement | null}
   */
  renderBoundary() {
    return this.shadowRoot?.querySelector('[part="base"]') ?? null;
  }

  // 13. Private methods

  #syncFormValue() {
    if (this.#name) {
      this._internals.setFormValue(this.#value);
    } else {
      this._internals.setFormValue(null);
    }
  }

  #emitClick(originalEvent) {
    return this.dispatchEvent(new CustomEvent('run-click', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { originalEvent },
    }));
  }

  #activate(originalEvent) {
    const notCancelled = this.#emitClick(originalEvent);
    if (!notCancelled) return;

    if (this.#type === 'submit' && this._internals.form) {
      this._internals.form.requestSubmit();
    } else if (this.#type === 'reset' && this._internals.form) {
      this._internals.form.reset();
    }
  }
}

customElements.define('run-button', RunButton);

export const BUTTON_TYPES = Object.freeze(['button', 'submit', 'reset']);
export const BUTTON_VARIANTS = Object.freeze(['primary', 'secondary', 'ghost', 'danger']);
export const BUTTON_SIZES = Object.freeze(['sm', 'md', 'lg']);
